export const officesyncSessionItems = [
	// Set 1 – MCQ
	{
		type: "SET",
		stimulus: {
			id: 40100,
			content: {
				instruction:
					"Listen to the audio and select the correct answer from the options provided.",
				passage: {
					title: "Accidental Mass Email Mishap – Venting Gone Wrong",
					paragraphs: [
						{
							text: "After waiting for hours for the IT team to respond, you sent an email venting your frustration—and mistakenly sent it to the entire company. This error has sparked a company-wide stir, and HR has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio1",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/officesync.mp3",
						type: "audio",
						transcript: `This is Cathy from HR.

Thank you for your email. I've had a chance to review it and wanted to reach out directly.

I want to acknowledge that I understand your frustration regarding the IT team's response time. That said, the decision to send your message to the entire company has created a number of challenges that now need to be addressed.

To move forward constructively, I'd appreciate it if you could walk me through what led to that decision and what steps you've taken since then to address the impact.

Please give me a call at your earliest convenience so we can discuss next steps together.

Looking forward to hearing from you.`,
					},
				],
			},
			metadata: {
				type: "READING",
				skill: "READING",
				title: "Reading Stimulus",
			},
		},
		items: [
			{
				id: 40101,
				type: "MCSS",
				content: {
					parentId: 40100,
					parentType: "SET",
					stem: "After receiving a voicemail from HR regarding your accidental mass email mistake, what is the most appropriate immediate action to address the situation?",
					choices: [
						{
							id: "c1a9d6e4-9f81-4d8f-8c6f-5a6e9b0b1c2d",
							stem: "Immediately send a company-wide apology email",
							text: "Immediately send a company-wide apology email",
							isCorrect: false,
						},
						{
							id: "a4b5c6d7-8e9f-1011-1213-141516171819",
							stem: "Contact your supervisor and HR to report the error",
							text: "Contact your supervisor and HR to report the error",
							isCorrect: true,
							explanation: `
In crisis situations such as an accidental mass email error, it is critical to take **immediate action** to control the damage and preserve the company’s reputation. Quick, decisive intervention helps to contain the fallout and maintain stakeholder trust.

**Contacting your supervisor and HR** to report the error is the most appropriate action because it ensures that the incident is **formally acknowledged** and managed through the proper channels. This proactive approach triggers **coordinated corrective measures** and facilitates **transparent communication**, which is essential for effective crisis resolution.

**Immediately sending a company-wide apology email** may draw excessive attention to the error and escalate the situation further; **attempting to recall the email** is generally ineffective once the message has been distributed; and **blaming the IT team** shifts responsibility away from you, which undermines accountability and fails to resolve the underlying issue.

**Key Takeaways:**
- **Immediate and transparent communication** is crucial for managing crises.
- **Taking responsibility** helps to activate coordinated responses and prevent further issues.

**References:**
- Coombs, W. T. (2014). *Ongoing crisis communication: Planning, managing, and responding* (4th ed.). Sage Publications. Retrieved from https://us.sagepub.com/en-us/nam/ongoing-crisis-communication/book232717
- Ulmer, R. R., Sellnow, T. L., & Seeger, M. W. (2018). *Effective crisis communication: Moving from crisis to opportunity* (3rd ed.). Sage Publications. Retrieved from https://us.sagepub.com/en-us/nam/effective-crisis-communication/book258775`,
						},
						{
							id: "b7c8d9e0-1a2b-3c4d-5e6f-778899aabbcc",
							stem: "Try to recall the email from the server",
							text: "Try to recall the email from the server",
							isCorrect: false,
						},
						{
							id: "d3c2b1a0-9988-7766-5544-33221100ffe0",
							stem: "Blame the IT team for the delay",
							text: "Blame the IT team for the delay",
							isCorrect: false,
						},
					],
					instruction: "Choose the best answer",
					highlights: null,
					media: null,
				},
				tags: [
					{
						id: 6479,
						type: "Skill",
						label: "Reading",
						breadcrumb: "Reading",
					},
				],
				metadata: {
					type: "MCSS",
					skill: "READING",
					title: "Multiple Choice Single Selection",
					topics: ["Crisis Communication"],
				},
				response: {
					maximumScore: 1,
					correctResponse: [{ id: "a4b5c6d7-8e9f-1011-1213-141516171819" }],
					numChoicesRequired: { max: 1, min: 1 },
				},
			},
		],
	},
	// Set 2 – Writing (Email Response)
	{
		type: "SET",
		stimulus: {
			id: 40110,
			content: {
				instruction: "Listen to the audio and then respond as instructed.",
				passage: {
					title: "Accidental Mass Email Mishap – Draft Your Email Response",
					paragraphs: [
						{
							text: "After waiting for hours for the IT team to respond, you sent an email venting your frustration—and mistakenly sent it to the entire company. This error has sparked a company-wide stir, and HR has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio2",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/officesync.mp3",
						type: "audio",
						transcript: `This is Cathy from HR.

Thank you for your email. I've had a chance to review it and wanted to reach out directly.

I want to acknowledge that I understand your frustration regarding the IT team's response time. That said, the decision to send your message to the entire company has created a number of challenges that now need to be addressed.

To move forward constructively, I'd appreciate it if you could walk me through what led to that decision and what steps you've taken since then to address the impact.

Please give me a call at your earliest convenience so we can discuss next steps together.

Looking forward to hearing from you.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Role-Play Writing Stimulus",
				topics: ["Crisis Communication"],
			},
		},
		items: [
			{
				id: 40111,
				versionId: 1,
				content: {
					parentId: 40110,
					parentType: "SET",
					stem: "Based on the voicemail from HR, draft an apology email that explains what went wrong, outlines your strategy to mitigate the fallout, and describes the measures you will implement to prevent similar mistakes in the future. Justify your approach.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 15,
					subskillsSchema: [
						{
							name: "Clarity and Organization",
							icon: "file-document-outline",
							description: "Structure your response clearly.",
							maxScore: 5,
						},
						{
							name: "Crisis Management",
							icon: "shield-check-outline",
							description: "Effectively address the crisis situation.",
							maxScore: 5,
						},
						{
							name: "Justification",
							icon: "check-circle-outline",
							description: "Provide rationale for your decisions.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Role-Play Writing Response",
					topics: ["Crisis Communication"],
				},
				score: null,
			},
		],
	},
	// Set 3 – Marking
	{
		type: "SET",
		stimulus: {
			id: 40120,
			content: {
				instruction:
					"Listen to the audio and then complete the role-play marking task.",
				passage: {
					title: "Email Client Screenshot Analysis",
					paragraphs: [
						{
							text: "After waiting for hours for the IT team to respond, you sent an email venting your frustration—and mistakenly sent it to the entire company. This error has sparked a company-wide stir, and HR has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio3",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/officesync.mp3",
						type: "audio",
						transcript: `This is Cathy from HR.

Thank you for your email. I've had a chance to review it and wanted to reach out directly.

I want to acknowledge that I understand your frustration regarding the IT team's response time. That said, the decision to send your message to the entire company has created a number of challenges that now need to be addressed.

To move forward constructively, I'd appreciate it if you could walk me through what led to that decision and what steps you've taken since then to address the impact.

Please give me a call at your earliest convenience so we can discuss next steps together.

Looking forward to hearing from you.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_MARKING_ANSWER_STIMULUS",
				skill: "MARKING",
				title: "Integrated Marking Answer Stimulus",
				topics: ["Privacy"],
			},
		},
		items: [
			{
				id: 40121,
				versionId: 1,
				content: {
					parentId: 40120,
					parentType: "SET",
					stem: "Review the attached screenshot of an email client. Circle the section that shows the unintended mass distribution",
					choices: [],
					instruction: "Circle and annotate the image as instructed.",
					media: [
						{
							id: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/final_email.png",
							url: "https://ajxjsnuynuxigljdrsyk.supabase.co/storage/v1/object/public/memorang-assets/demo/acme/final_email.png",
							type: "image",
						},
					],
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Marking",
						breadcrumb: "Marking",
					},
				],
				response: {
					maximumScore: 10,
					subskillsSchema: [
						{
							name: "Accuracy",
							icon: "crosshairs-gps",
							maxScore: 5,
							description: "Precisely identify the error area.",
						},
						{
							name: "Speed",
							icon: "timer-outline",
							maxScore: 5,
							description: "Identify the error area quickly.",
						},
					],
				},
				metadata: {
					type: "MARKING_ANSWER",
					skill: "MARKING",
					title: "Email Distribution Annotation",
					topics: ["Privacy"],
				},
				score: null,
			},
		],
	},
	// Set 4 – Audio
	{
		type: "SET",
		stimulus: {
			id: 40130,
			content: {
				instruction: "Listen to the audio and record your answer.",
				passage: {
					title: "Audio Scenario: Addressing HR",
					paragraphs: [
						{
							text: "After waiting for hours for the IT team to respond, you sent an email venting your frustration—and mistakenly sent it to the entire company. This error has sparked a company-wide stir, and HR has left a voicemail below:",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio4",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/officesync.mp3",
						type: "audio",
						transcript: `This is Cathy from HR.

Thank you for your email. I've had a chance to review it and wanted to reach out directly.

I want to acknowledge that I understand your frustration regarding the IT team's response time. That said, the decision to send your message to the entire company has created a number of challenges that now need to be addressed.

To move forward constructively, I'd appreciate it if you could walk me through what led to that decision and what steps you've taken since then to address the impact.

Please give me a call at your earliest convenience so we can discuss next steps together.

Looking forward to hearing from you.`,
					},
				],
			},
			metadata: {
				type: "LS_STIMULUS",
				skill: "SPEAKING",
				title: "Listen Speak Stimulus",
				topics: ["Accountability"],
			},
		},
		items: [
			{
				id: 40131,
				type: "AUDIO_INPUT_EMOTION",
				content: {
					parentId: 40130,
					parentType: "SET",
					stem: "Record an audio response in which you take full responsibility for the mistake, explain what went wrong, outline your corrective actions, and describe how you will prevent similar incidents in the future.",
					choices: [],
					instruction: "Record your answer.",
					media: null,
				},
				tags: [
					{
						id: 6481,
						type: "Skill",
						label: "Speaking",
						breadcrumb: "Speaking",
					},
				],
				response: {
					maximumScore: 30,
					maximumDuration: 40,
					subskillsSchema: [
						{
							maxScore: 5,
							name: "Communication & Interpersonal Skills",
							icon: "message-outline",
							description: "Demonstrate effective verbal communication.",
						},
						{
							maxScore: 5,
							name: "Responsibility",
							icon: "account-check-outline",
							description: "Show accountability for the incident.",
						},
						{
							maxScore: 5,
							name: "Clarity",
							icon: "microphone-outline",
							description: "Speak clearly and coherently.",
						},
						{
							maxScore: 5,
							name: "Professionalism",
							icon: "briefcase-outline",
							description: "Maintain a professional tone.",
						},
						{
							maxScore: 5,
							name: "Solution Orientation",
							icon: "arrow-right-circle-outline",
							description: "Present clear corrective actions.",
						},
						{
							maxScore: 5,
							name: "Empathy",
							icon: "heart-outline",
							description:
								"Convey understanding and empathy toward stakeholders.",
						},
					],
				},
				metadata: {
					type: "AUDIO_INPUT_EMOTION",
					skill: "SPEAKING",
					title: "Audio Response Item",
					topics: ["Accountability"],
				},
				score: null,
			},
		],
	},
	// Set 5 – Writing (Post-Incident Report)
	{
		type: "SET",
		stimulus: {
			id: 40140,
			content: {
				instruction: "Listen to the audio and then respond as instructed.",
				passage: {
					title: "Accidental Mass Email Mishap – Post-Incident Report",
					paragraphs: [
						{
							text: "After waiting for hours for the IT team to respond, you sent an email venting your frustration—and mistakenly sent it to the entire company. This error has sparked a company-wide stir, and HR has left a voicemail below. Based on this incident, compose a comprehensive post-incident report from your perspective that includes a timeline of events, an assessment of the impact, the corrective actions taken, and recommendations for updating email protocols to prevent similar incidents in the future.",
							number: 1,
						},
					],
				},
				media: [
					{
						id: "audio5a",
						url: "https://services.memorang.com/storage/v1/object/public/memorang-assets/demo/acme/officesync.mp3",
						type: "audio",
						transcript: `This is Cathy from HR.

Thank you for your email. I've had a chance to review it and wanted to reach out directly.

I want to acknowledge that I understand your frustration regarding the IT team's response time. That said, the decision to send your message to the entire company has created a number of challenges that now need to be addressed.

To move forward constructively, I'd appreciate it if you could walk me through what led to that decision and what steps you've taken since then to address the impact.

Please give me a call at your earliest convenience so we can discuss next steps together.

Looking forward to hearing from you.`,
					},
				],
			},
			metadata: {
				type: "INTEGRATED_WRITING_STIMULUS",
				skill: "WRITING",
				title: "Post-Incident Report Writing Stimulus",
				topics: ["Crisis Communication"],
			},
		},
		items: [
			{
				id: 40141,
				versionId: 1,
				content: {
					parentId: 40140,
					parentType: "SET",
					stem: "Based on the HR voicemail, compose a comprehensive post-incident report that includes a timeline of events leading up to the email mishap, an assessment of the impact on internal communication and reputation, the corrective actions taken, and detailed recommendations for updating email protocols to prevent similar incidents in the future. Justify your recommendations.",
					choices: [],
					instruction: "Write your response in the text box provided.",
					media: null,
				},
				tags: [
					{
						id: 6480,
						type: "Skill",
						label: "Writing",
						breadcrumb: "Writing",
					},
				],
				response: {
					maximumScore: 20,
					subskillsSchema: [
						{
							name: "Organization",
							icon: "file-tree-outline",
							description: "Organize your report logically.",
							maxScore: 5,
						},
						{
							name: "Accountability",
							icon: "check-circle-outline",
							description: "Demonstrate responsibility and transparency.",
							maxScore: 5,
						},
						{
							name: "Actionable Recommendations",
							icon: "lightbulb-outline",
							description: "Offer practical measures for prevention.",
							maxScore: 5,
						},
						{
							name: "Detail Orientation",
							icon: "file-document-outline",
							description: "Include all relevant details in your report.",
							maxScore: 5,
						},
					],
				},
				metadata: {
					type: "INTEGRATED_WRITING_RESPONSE",
					skill: "WRITING",
					title: "Post-Incident Report Response",
					topics: ["Crisis Communication"],
					taskType: "default",
				},
				score: null,
			},
		],
	},
];
